import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete } from '../lib/api';

export interface SendBatch {
  id: string;
  userId: string;
  teamId: string;
  pressListId: string | null;
  emailAccountId: string;
  status: 'queued' | 'sending' | 'paused' | 'completed' | 'cancelled';
  totalCount: number;
  sentCount: number;
  failedCount: number;
  cancelledCount: number;
  staggerMinSeconds: number;
  staggerMaxSeconds: number;
  createdAt: string;
  updatedAt: string;
}

export interface BatchProgress {
  queued: number;
  sending: number;
  sent: number;
  failed: number;
  cancelled: number;
  total: number;
}

export interface SendBatchDetail {
  batch: SendBatch;
  progress: BatchProgress;
  estimatedCompletionAt: string | null;
}

export interface Message {
  id: string;
  userId: string;
  contactId: string;
  pressListId: string | null;
  emailAccountId: string | null;
  sendBatchId: string | null;
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  toEmail: string;
  status: 'draft' | 'queued' | 'sending' | 'sent' | 'delivered' | 'opened' | 'replied' | 'bounced' | 'failed' | 'cancelled';
  scheduledAt: string | null;
  sentAt: string | null;
  openedAt: string | null;
  openCount: number;
  threadId: string | null;
  messageId: string | null;
  inReplyTo: string | null;
  failureReason: string | null;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
  contactFirstName?: string | null;
  contactLastName?: string | null;
}

export interface MessagesResponse {
  messages: Message[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

// ─── Send batch queries ────────────────────────────────────────────────────

export function useSendBatch(id: string, polling = false) {
  return useQuery({
    queryKey: ['send-batch', id],
    queryFn: () => apiGet<SendBatchDetail>(`/send-batches/${id}`),
    refetchInterval: polling ? 5000 : false,
    enabled: !!id,
  });
}

export function usePauseBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ ok: boolean; status: string }>(`/send-batches/${id}/pause`),
    onSuccess: (_data, id) => qc.invalidateQueries({ queryKey: ['send-batch', id] }),
  });
}

export function useResumeBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ ok: boolean; status: string; requeued: number }>(`/send-batches/${id}/resume`),
    onSuccess: (_data, id) => qc.invalidateQueries({ queryKey: ['send-batch', id] }),
  });
}

export function useCancelBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ ok: boolean; status: string; cancelled: number }>(`/send-batches/${id}/cancel`),
    onSuccess: (_data, id) => qc.invalidateQueries({ queryKey: ['send-batch', id] }),
  });
}

// ─── Messages queries ──────────────────────────────────────────────────────

export interface MessageQueryParams {
  contactId?: string;
  pressListId?: string;
  sendBatchId?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export function useMessages(params: MessageQueryParams = {}) {
  return useQuery({
    queryKey: ['messages', params],
    queryFn: () => apiGet<MessagesResponse>('/messages', params as Record<string, string | number | boolean | undefined>),
  });
}

export function useCancelMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/messages/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });
}

// ─── Send press list ───────────────────────────────────────────────────────

export interface SendPressListInput {
  emailAccountId: string;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  staggerMinSeconds?: number;
  staggerMaxSeconds?: number;
  dailyLimit?: number;
}

export function useSendPressList(pressListId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendPressListInput) =>
      apiPost<{ sendBatchId: string; queued: number }>(`/press-lists/${pressListId}/send`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['press-list', pressListId] });
    },
  });
}
